/**
 * Forgot Password Page - Olympic Fit Wellness
 * Send password reset code via email
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState('email'); // email | code | password | success
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSendCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to send reset code');
      }

      setStep('code');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, code, new_password: newPassword })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to reset password');
      }

      setStep('success');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="forgot-password-page">
      {/* Left Side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1571902943202-507ec2618e8f?w=1200&q=80"
          alt="Olympic Fit Wellness"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/80 to-neutral-900/40" />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="max-w-md">
            <Link to="/" className="inline-block mb-8">
              <span className="text-4xl font-heading font-bold text-white">
                <span>O</span>
                <span>F</span>
                <span className="text-red-500">W</span>
              </span>
            </Link>
            <h1 className="text-4xl font-heading font-bold text-white mb-4">
              Reset Password
            </h1>
            <p className="text-xl text-white/70">
              We'll send you a verification code to reset your password.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <Link to="/" className="lg:hidden inline-block mb-8">
            <span className="text-4xl font-heading font-bold text-neutral-900">
              <span>O</span>
              <span>F</span>
              <span className="text-red-500">W</span>
            </span>
          </Link>

          {/* Back Link */}
          <Link to="/login" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-700 mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          {step === 'success' ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">Password Reset!</h2>
              <p className="text-neutral-500 mb-8">
                Your password has been reset successfully. You can now sign in with your new password.
              </p>
              <Link to="/login" className="btn-primary inline-block">
                Sign In
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-3xl font-heading font-bold text-neutral-900 mb-2">
                {step === 'email' ? 'Forgot Password?' : 'Reset Password'}
              </h2>
              <p className="text-neutral-500 mb-8">
                {step === 'email' 
                  ? 'Enter your email and we\'ll send you a verification code.'
                  : 'Enter the code we sent and your new password.'}
              </p>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
                  <AlertCircle className="w-5 h-5 flex-shrink-0" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {step === 'email' && (
                <form onSubmit={handleSendCode} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="input-field pl-10"
                        placeholder="you@example.com"
                        required
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="send-code-button"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Sending...' : 'Send Reset Code'}
                  </button>
                </form>
              )}

              {step === 'code' && (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Verification Code
                    </label>
                    <input
                      type="text"
                      value={code}
                      onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      className="input-field text-center text-2xl tracking-[0.5em] font-bold"
                      placeholder="000000"
                      maxLength={6}
                      required
                      data-testid="code-input"
                    />
                    <p className="text-sm text-neutral-500 mt-2">
                      Enter the 6-digit code sent to {email}
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="input-field"
                      placeholder="Enter new password"
                      minLength={8}
                      required
                      data-testid="new-password-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">
                      Confirm Password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="input-field"
                      placeholder="Confirm new password"
                      minLength={8}
                      required
                      data-testid="confirm-password-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading || code.length !== 6}
                    className="btn-primary w-full disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="reset-password-button"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </button>

                  <button
                    type="button"
                    onClick={() => setStep('email')}
                    className="w-full text-center text-neutral-500 hover:text-neutral-700 text-sm"
                  >
                    Didn't receive the code? Try again
                  </button>
                </form>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
